import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { Bars3Icon, DocumentTextIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import ExcelExport from "../components/ExcelExport";
import TableHeaderFilter from "../components/TableHeaderfilter";
import MonthYearPicker from "../components/MonthYearPicker";
import axios from "axios";
import { SERVER_URL } from "../config";

const CURRENT_YEAR = new Date().getFullYear();

/* ── Status badge ─────────────────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const map = {
    approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    rejected: "bg-rose-50 text-rose-700 border border-rose-200",
    pending:  "bg-amber-50 text-amber-700 border border-amber-200",
  };
  const dots = {
    approved: "bg-emerald-500",
    rejected: "bg-rose-500",
    pending:  "bg-amber-400",
  };
  const key = status?.toLowerCase();
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${map[key] || "bg-slate-100 text-slate-600 border border-slate-200"}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dots[key] || "bg-slate-400"}`} />
      {status}
    </span>
  );
};

/* ── Tab config ───────────────────────────────────────────────────────────── */
const TABS = [
  { key: "Pending",  label: "Pending",  active: "bg-amber-500 text-white border-amber-500 shadow-sm shadow-amber-200",   idle: "text-slate-500 border-slate-200 bg-white hover:bg-amber-50 hover:text-amber-600 hover:border-amber-300" },
  { key: "Approved", label: "Approved", active: "bg-emerald-600 text-white border-emerald-600 shadow-sm shadow-emerald-200", idle: "text-slate-500 border-slate-200 bg-white hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-300" },
  { key: "Rejected", label: "Rejected", active: "bg-rose-500 text-white border-rose-500 shadow-sm shadow-rose-200",      idle: "text-slate-500 border-slate-200 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-300" },
];

/* ════════════════════════════════════════════════════════════════════════════ */
const LeaveApplications = () => {
  const [applications, setApplications]         = useState([]);
  const [search, setSearch]                     = useState("");
  const [filterStatus, setFilterStatus]         = useState("Pending");
  const [isModalOpen, setIsModalOpen]           = useState(false);
  const [selectedEmpStats, setSelectedEmpStats] = useState(null);
  const [loadingModal, setLoadingModal]         = useState(false);
  const [empIdFilter, setEmpIdFilter]           = useState([]);
  const [empNameFilter, setEmpNameFilter]       = useState([]);
  const [typeFilter, setTypeFilter]             = useState([]);
  const [sidebarOpen, setSidebarOpen]           = useState(false);
  const [pickedMonth, setPickedMonth]           = useState(null);
  const [pickedYear, setPickedYear]             = useState(null);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => { document.body.style.overflow = "auto"; };
  }, [isModalOpen]);

  const fetchLeaveHistory = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/DataFetch/fetchAllLeaveApplication.php`);
      setApplications(response.data?.list ?? []);
    } catch (error) {
      console.error("Failed to fetch leave history", error);
    }
  };

  const updateStatus = async (id, newStatus, adminId) => {
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/updateStatus.php`, {
        leave_id: id, status: newStatus, admin_id: adminId,
      });
      if (res.data?.status === "success") {
        setApplications((prev) =>
          prev.map((app) => app.leave_id === id ? { ...app, status: newStatus } : app)
        );
      }
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const uniqueOptions = (data, key) =>
    [...new Set(data.map((d) => (d[key] ?? "").toString().trim()).filter(Boolean))]
      .sort().map((v) => ({ label: v, value: v }));

  const empIdOptions   = uniqueOptions(applications, "employee_id");
  const empNameOptions = uniqueOptions(applications, "employee_name");
  const typeOptions    = uniqueOptions(applications, "leave_name");

  const handleRowClick = async (empId) => {
    setLoadingModal(true);
    try {
      const res = await axios.get(`${SERVER_URL}/Rahul/fetchAllEmployeeLeaveStat.php`, {
        params: { employee_id: empId, year: CURRENT_YEAR },
      });
      if (res.data?.status === true && res.data?.summary) {
        const { summary, history } = res.data;
        setSelectedEmpStats({
          total_used:   Number(summary.total_used),
          total_alloc:  Number(summary.total_casual) + Number(summary.total_sick),
          casual_used:  Number(summary.used_casual),
          casual_alloc: Number(summary.total_casual),
          sick_used:    Number(summary.used_sick),
          sick_alloc:   Number(summary.total_sick),
          history:      history || [],
        });
        setIsModalOpen(true);
      } else {
        setSelectedEmpStats(null);
      }
    } catch (err) {
      console.error("Employee stats API failed", err);
      setSelectedEmpStats(null);
    } finally {
      setLoadingModal(false);
    }
  };

  useEffect(() => { fetchLeaveHistory(); }, []);

  const filteredApplications = applications
    .filter((leave) => leave.status?.toLowerCase() === filterStatus.toLowerCase())
    .filter((leave) =>
      leave.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
      leave.employee_id?.toLowerCase().includes(search.toLowerCase())
    )
    .filter((leave) => {
      if (pickedMonth !== null && pickedYear !== null) {
        const monthStart = new Date(pickedYear, pickedMonth - 1, 1);
        const monthEnd   = new Date(pickedYear, pickedMonth, 0, 23, 59, 59, 999);
        const leaveStart = new Date(leave.first_date);
        const leaveEnd   = new Date(leave.last_date);
        if (leaveStart > monthEnd || leaveEnd < monthStart) return false;
      }
      const empId   = (leave.employee_id  ?? "").toString();
      const empName = (leave.employee_name ?? "").toString();
      const type    = (leave.leave_name   ?? "").toString();
      return (
        (empIdFilter.length   === 0 || empIdFilter.includes(empId)) &&
        (empNameFilter.length === 0 || empNameFilter.includes(empName)) &&
        (typeFilter.length    === 0 || typeFilter.includes(type))
      );
    });

  const excelColumns = [
    { header: "Employee ID",   key: "employee_id",   type: "text" },
    { header: "Employee Name", key: "employee_name", type: "text" },
    { header: "From Date",     key: "first_date",    type: "text" },
    { header: "To Date",       key: "last_date",     type: "text" },
    { header: "Total Days",    key: "total_days",    type: "number" },
    { header: "Leave Type",    key: "leave_name",    type: "text" },
    { header: "Status",        key: "status",        type: "text" },
  ];

  const inputCls = "bg-indigo-50/40 border border-indigo-100 rounded-xl px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white transition-all";

  /* ════════════════════════════════════════════════════════════════════════ */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .page-font { font-family: 'DM Sans', sans-serif; }
        .page-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
        .page-scroll::-webkit-scrollbar-track { background: transparent; }
        .page-scroll::-webkit-scrollbar-thumb { background: #c7d2fe; border-radius: 99px; }
        .top-accent { background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%); }
        .header-bg { background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%); }
        .card-bg { background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%); }
      `}</style>

      <div className="min-h-screen w-full page-font" style={{ background: "#f5f7ff" }}>
        <div className="fixed top-0 left-0 right-0 h-0.5 z-50 top-accent" />

        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={() => { localStorage.clear(); }} />

        {/* ── STICKY HEADER ── */}
        <header className="sticky top-0 z-20 border-b border-indigo-100/60 header-bg shadow-sm shadow-indigo-100/50">
          <div className="px-3 sm:px-5 lg:px-7 py-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-all border border-indigo-100 flex-shrink-0"
              >
                <Bars3Icon className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center shadow-md shadow-indigo-200 flex-shrink-0">
                  <DocumentTextIcon className="w-4 h-4 text-white" />
                </div>
                <h1 className="text-sm sm:text-base lg:text-lg font-bold text-slate-800 tracking-tight whitespace-nowrap">
                  Leave Applications
                </h1>
              </div>
            </div>
            <ExcelExport data={filteredApplications} columns={excelColumns} fileName="LeaveReport.xlsx" reportTitle="Leave Report" />
          </div>
        </header>

        {/* ── MAIN CONTENT ── */}
        <div className="flex flex-col gap-3 sm:gap-4 p-3 sm:p-4 lg:p-5">

          {/* ── FILTER BAR ── */}
          <div className="card-bg rounded-2xl p-3 sm:p-4 border border-indigo-100/60 shadow-sm shadow-indigo-100/40">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              {/* Status tabs */}
              <div className="flex items-center gap-2 flex-wrap">
                {TABS.map(({ key, label, active, idle }) => (
                  <button
                    key={key}
                    onClick={() => setFilterStatus(key)}
                    className={`px-4 py-1.5 rounded-xl text-xs font-bold border transition-all duration-200 whitespace-nowrap ${filterStatus === key ? active : idle}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {/* Controls */}
              <div className="flex flex-col xs:flex-row gap-2 items-stretch xs:items-center">
                <MonthYearPicker
                  selectedMonth={pickedMonth}
                  selectedYear={pickedYear}
                  onChange={({ month, year }) => { setPickedMonth(month); setPickedYear(year); }}
                />
                <input
                  className={`${inputCls} w-full xs:w-52`}
                  placeholder="Search by Emp ID or Name"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── TABLE ── */}
          <div className="rounded-2xl overflow-hidden bg-white border border-indigo-100/60 shadow-sm shadow-indigo-100/40">
            <div className="overflow-x-auto overflow-y-auto max-h-[520px] page-scroll">
              <table className="min-w-[700px] w-full text-sm">
                <thead className="sticky top-0 z-10" style={{ background: "#f8faff" }}>
                  <tr>
                    <th className="p-3 text-left border-b border-indigo-100/60">
                      <TableHeaderFilter label="Emp ID" options={empIdOptions} selectedValues={empIdFilter} onChange={setEmpIdFilter} />
                    </th>
                    <th className="p-3 text-left border-b border-indigo-100/60">
                      <TableHeaderFilter label="Name" options={empNameOptions} selectedValues={empNameFilter} onChange={setEmpNameFilter} />
                    </th>
                    {["From", "To", "Days", "Type", "Reason", "Status", "Actions"].map(h => (
                      <th key={h} className="p-3 text-left border-b border-indigo-100/60">
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{h}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredApplications.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center p-10 text-slate-400 text-sm">No leave applications found.</td>
                    </tr>
                  ) : (
                    filteredApplications.map((leave) => (
                      <tr
                        key={leave.leave_id}
                        onClick={() => handleRowClick(leave.employee_id)}
                        className="border-t border-slate-100 cursor-pointer transition-colors hover:bg-indigo-50/40"
                      >
                        <td className="p-3 text-slate-500 text-xs font-medium">{leave.employee_id}</td>
                        <td className="p-3 text-slate-800 text-xs font-semibold">{leave.employee_name}</td>
                        <td className="p-3 text-slate-500 text-xs">{leave.first_date}</td>
                        <td className="p-3 text-slate-500 text-xs">{leave.last_date}</td>
                        <td className="p-3 text-center">
                          <span className="text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-md">{leave.total_days}</span>
                        </td>
                        <td className="p-3 text-slate-600 text-xs">{leave.leave_name}</td>
                        <td className="p-3 text-slate-500 text-xs max-w-[140px] truncate">{leave.reason}</td>
                        <td className="p-3"><StatusBadge status={leave.status} /></td>
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          {leave.status?.toLowerCase() === "pending" ? (
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => updateStatus(leave.leave_id, "Approved", leave.employee_id)}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                              >
                                <CheckCircleIcon className="w-3 h-3" />
                                <span className="hidden sm:inline">Approve</span>
                              </button>
                              <button
                                onClick={() => updateStatus(leave.leave_id, "Rejected", leave.employee_id)}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-rose-700 border border-rose-200 bg-rose-50 hover:bg-rose-100 transition-colors"
                              >
                                <XCircleIcon className="w-3 h-3" />
                                <span className="hidden sm:inline">Reject</span>
                              </button>
                            </div>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── LEAVE HISTORY MODAL ── */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(8px)" }}
        >
          <div className="relative flex flex-col rounded-2xl border border-indigo-100 w-full max-w-sm bg-white shadow-2xl shadow-indigo-100/50"
            style={{ maxHeight: "90vh", padding: "24px" }}>

            <div className="flex-shrink-0 flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <DocumentTextIcon className="w-4 h-4 text-indigo-500" />
                </div>
                <h3 className="text-sm font-bold text-slate-800">
                  Leave History <span className="text-xs font-semibold text-slate-400 ml-1">({CURRENT_YEAR})</span>
                </h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-8 h-8 rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center transition-all"
              >✕</button>
            </div>

            <div className="flex-1 overflow-y-auto page-scroll">
              {loadingModal ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2">
                  <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-slate-400">Loading…</span>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    {[
                      { label: "Total",  used: selectedEmpStats?.total_used,  alloc: selectedEmpStats?.total_alloc,  bg: "bg-indigo-50 border-indigo-100", accent: "text-indigo-500", val: "text-indigo-800" },
                      { label: "Casual", used: selectedEmpStats?.casual_used, alloc: selectedEmpStats?.casual_alloc, bg: "bg-amber-50 border-amber-100",   accent: "text-amber-500",  val: "text-amber-800" },
                      { label: "Sick",   used: selectedEmpStats?.sick_used,   alloc: selectedEmpStats?.sick_alloc,   bg: "bg-rose-50 border-rose-100",     accent: "text-rose-500",   val: "text-rose-800" },
                    ].map(({ label, used, alloc, bg, accent, val }) => (
                      <div key={label} className={`${bg} border rounded-xl px-3 py-3 text-center`}>
                        <p className={`text-[9px] font-bold uppercase tracking-widest ${accent}`}>{label}</p>
                        <p className={`text-lg font-extrabold ${val}`}>{used}/{alloc}</p>
                      </div>
                    ))}
                  </div>

                  <div className="rounded-xl overflow-hidden border border-indigo-100">
                    <div className="overflow-y-auto max-h-[220px] page-scroll">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 z-10" style={{ background: "#f8faff" }}>
                          <tr>
                            {["Dates", "Days", "Type", "Status"].map(h => (
                              <th key={h} className="p-2 border-b border-indigo-100 text-center">
                                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{h}</span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {selectedEmpStats?.history?.length > 0 ? (
                            selectedEmpStats.history.map((h, i) => (
                              <tr key={i} className="border-t border-slate-100 hover:bg-indigo-50/40 transition-colors">
                                <td className="p-2 text-slate-500 text-center whitespace-nowrap">{h.start_date} – {h.end_date}</td>
                                <td className="p-2 text-slate-700 text-center font-bold">{h.total_days}</td>
                                <td className="p-2 text-slate-600 text-center">{h.leave_name}</td>
                                <td className="p-2 text-center"><StatusBadge status={h.status} /></td>
                              </tr>
                            ))
                          ) : (
                            <tr><td colSpan={4} className="p-4 text-center text-slate-400">No leave history found</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex-shrink-0 flex justify-end mt-4 pt-3 border-t border-slate-100">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-all"
              >Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeaveApplications;